import * as React from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "@/lib/convexApi";
import { useSession } from "@/lib/auth-client";

// Stable identity context — only changes on login/logout or role change.
// Hooks that just need authUserId subscribe here and avoid re-renders from
// unrelated profile field updates.
type AuthIdentity = {
  authUserId: string | undefined;
  userRole: string | undefined;
  isAuthenticated: boolean;
  isPending: boolean;
  isConvexAuthenticated: boolean;
  isConvexLoading: boolean;
  isAuthBridgeReady: boolean;
};

const AuthIdentityContext = React.createContext<AuthIdentity | null>(null);

// Full profile context — includes session, user object, profile doc.
// Only components that render user details (layout, profile page) need this.
function useFullProfileValue() {
  const { data: session, isPending: isSessionPending } = useSession();
  const {
    isAuthenticated: isConvexAuthenticated,
    isLoading: isConvexLoading,
  } = useConvexAuth();
  const user = session?.user;
  const isAuthenticated = Boolean(user);
  const isAuthBridgeReady =
    !isSessionPending &&
    !isConvexLoading &&
    (!isAuthenticated || isConvexAuthenticated);

  const profile = useQuery(
    api.users.getMyProfile,
    isAuthenticated && isConvexAuthenticated ? {} : "skip",
  );

  const userRole = (profile?.role as string | undefined) ?? undefined;
  const authUserId = (user?.id as string | undefined) ?? undefined;

  return {
    session,
    user,
    profile,
    userRole,
    authUserId,
    isAuthenticated,
    isPending: isSessionPending,
    isConvexAuthenticated,
    isConvexLoading,
    isAuthBridgeReady,
  };
}

type FullProfileContextValue = ReturnType<typeof useFullProfileValue>;

const FullProfileContext = React.createContext<FullProfileContextValue | null>(
  null,
);

export function UserProfileProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const full = useFullProfileValue();

  // Stable identity — only changes when these primitive values change
  const identity = React.useMemo<AuthIdentity>(
    () => ({
      authUserId: full.authUserId,
      userRole: full.userRole,
      isAuthenticated: full.isAuthenticated,
      isPending: full.isPending,
      isConvexAuthenticated: full.isConvexAuthenticated,
      isConvexLoading: full.isConvexLoading,
      isAuthBridgeReady: full.isAuthBridgeReady,
    }),
    [
      full.authUserId,
      full.userRole,
      full.isAuthenticated,
      full.isPending,
      full.isConvexAuthenticated,
      full.isConvexLoading,
      full.isAuthBridgeReady,
    ],
  );

  return (
    <AuthIdentityContext.Provider value={identity}>
      <FullProfileContext.Provider value={full}>
        {children}
      </FullProfileContext.Provider>
    </AuthIdentityContext.Provider>
  );
}

/** Lightweight hook — only re-renders when authUserId/userRole/auth state change. */
export function useAuthIdentity(): AuthIdentity {
  const ctx = React.useContext(AuthIdentityContext);
  if (ctx === null) {
    throw new Error(
      "useAuthIdentity must be used within a UserProfileProvider",
    );
  }
  return ctx;
}

/** Full profile hook — re-renders on any profile/session change. */
export function useUserProfileContext() {
  const ctx = React.useContext(FullProfileContext);
  if (ctx === null) {
    throw new Error(
      "useUserProfileContext must be used within a UserProfileProvider",
    );
  }
  const {
    session,
    user,
    profile,
    userRole,
    authUserId,
    isAuthenticated,
    isPending,
    isConvexAuthenticated,
    isConvexLoading,
    isAuthBridgeReady,
  } = ctx;
  return {
    session,
    user,
    profile,
    userRole,
    authUserId,
    isAuthenticated,
    isPending,
    isConvexAuthenticated,
    isConvexLoading,
    isAuthBridgeReady,
    isProfileLoading: isAuthBridgeReady && isAuthenticated && profile === undefined,
  };
}
